/**
 * Client IA de la régie : réponses validées et cache isolé par compte/galerie.
 * Les mutations conservent cette portée et la version de la voix. Les montants
 * restent en cents de dollar dans le contrat ; les écrans font l’affichage.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  reponseEtatIaSchema,
  reponseAppelsIaSchema,
  reponseVoixIaSchema,
  reponseApercuIaSchema,
  type VoixIa,
} from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import { useMutation } from './mutation.js';

export type Voix = ReturnType<typeof reponseVoixIaSchema.parse>;

export type EtatIa = ReturnType<typeof reponseEtatIaSchema.parse>;

export interface FiltresIa {
  page: number;
  usage: string;
  etat: string;
  mois: string;
}

const chemin = (g: string, suite: string) => `/g/${encodeURIComponent(g)}/ia/${suite}`;

export const iaApi = {
  etat: async (g: string, appel: AppelApi) =>
    reponseEtatIaSchema.parse(await api.get(chemin(g, 'etat'), { galerie: g, ...appel })),
  voix: async (g: string, appel: AppelApi) =>
    reponseVoixIaSchema.parse(await api.get(chemin(g, 'voix'), { galerie: g, ...appel })),
  appels: async (g: string, filtres: FiltresIa, appel: AppelApi) => {
    const params = new URLSearchParams();
    Object.entries(filtres).forEach(([k, v]) => {
      if (v) params.set(k, String(v));
    });
    return reponseAppelsIaSchema.parse(
      await api.get(`${chemin(g, 'appels')}?${params}`, { galerie: g, ...appel }),
    );
  },
  enregistrer: async (g: string, voix: VoixIa & { version: number }, appel: AppelApi) =>
    reponseVoixIaSchema.parse(await api.put(chemin(g, 'voix'), voix, { galerie: g, ...appel })),
  apercu: async (g: string, question: string, appel: AppelApi) =>
    reponseApercuIaSchema.parse(
      await api.post(chemin(g, 'voix/apercu'), { question }, { galerie: g, ...appel }),
    ),
};

export function useEtatIa(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'ia', 'etat'),
    queryFn: (c) => iaApi.etat(g, appelDeRequete(c)),
    refetchInterval: 30_000,
  });
}

export function useVoixIa(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'ia', 'voix'),
    queryFn: (c) => iaApi.voix(g, appelDeRequete(c)),
  });
}

export function useAppelsIa(g: string, filtres: FiltresIa) {
  return useQuery({
    queryKey: cleMetier(g, 'ia', 'appels', filtres),
    queryFn: (c) => iaApi.appels(g, filtres, appelDeRequete(c)),
  });
}

export function useEnregistrerVoix(g: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (voix: VoixIa & { version: number }, appel) => iaApi.enregistrer(g, voix, appel),
      onSuccess: (voix) => client.setQueryData(cleMetier(g, 'ia', 'voix'), voix),
    }),
  );
}

export function useApercuIa(g: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (question: string, appel) => iaApi.apercu(g, question, appel),
      onSettled: () => {
        void client.invalidateQueries({ queryKey: cleMetier(g, 'ia', 'etat') });
        void client.invalidateQueries({ queryKey: cleMetier(g, 'ia', 'appels') });
      },
    }),
  );
}
